// The inputs inside the built-in app connect form — one per field the app declares.
//
// Pulled out of AppCredentialsForm so it can be rendered in a test. The form itself holds the
// hooks (values, busy, error), so it cannot be called as a plain function; this part is pure,
// which is how the repo tests rendering without a DOM library (see
// AddProviderInstanceWizardSteps.test.tsx — call the component, walk the returned tree).
//
// Worth testing rather than eyeballing, because every built-in app's connect form is generated
// from `fields` served by GET /api/morrow/apps. One mistake here — a secret rendered as a plain
// text input, a help string dropped — applies to every marketplace connector at once, and it is
// the exact screen where someone pastes an API secret.
import type { ReactElement } from "react";

import { Input } from "../ui/input";
import type { AppField } from "./AppConnectPage";

export function CredentialFieldInputs({
  fields,
  values,
  onChange,
}: {
  readonly fields: readonly AppField[];
  readonly values: Readonly<Record<string, string>>;
  readonly onChange: (key: string, value: string) => void;
}): ReactElement {
  return (
    <div className="flex flex-col gap-3">
      {fields.map((f) => (
        <label key={f.key} className="flex flex-col gap-1">
          <span className="text-[11px] font-medium text-muted-foreground">
            {f.label}
            {f.required ? "" : " (optional)"}
          </span>
          <Input
            type={f.type}
            className="h-9"
            placeholder={f.placeholder}
            value={values[f.key] ?? ""}
            onChange={(e) => onChange(f.key, e.target.value)}
            // A marketplace API secret must never land in a browser's saved-password or
            // autofill store, and must never be offered back as a suggestion.
            autoComplete={f.type === "password" ? "new-password" : "off"}
          />
          {f.help ? <span className="text-[11px] text-muted-foreground/80">{f.help}</span> : null}
        </label>
      ))}
    </div>
  );
}
